import { ref, computed } from 'vue';
import { defineStore } from 'pinia';
import { useAppSnackbar } from '@/stores/app/snackbar';
import {
  searchJobPostsForWizard,
  getJobPostForWizard,
  recruitJsonGetTemplates,
  createJobPostFromWizard,
} from '@/api/recruitJson';

export const useCreateJobPostWizard = defineStore('createJobPostWizard', () => {
  const { addErrorSnackbar, addMultilineErrorSnackbar } = useAppSnackbar();

  // ─── step ────────────────────────────────────────────────────────────────────
  const step = ref(1);

  // ─── step 1: form data ───────────────────────────────────────────────────────
  const formTitle = ref('');
  const formJobCode = ref('');
  const formLocation = ref('');

  // ─── step 1: search ──────────────────────────────────────────────────────────
  const searchResults = ref([]);
  const searchLoading = ref(false);

  // ─── step 1 & 2: job preview ─────────────────────────────────────────────────
  const previewJob = ref(null);
  const previewLoading = ref(false);
  const selectedJobId = ref(null);

  // ─── step 2: templates ────────────────────────────────────────────────────────
  const templates = ref([]);
  const templatesLoading = ref(false);
  const selectedTemplateId = ref(null);
  const templatePreview = ref(null);
  const templatePreviewLoading = ref(false);

  /**
   * Merged preview for Step 2: template header/footer fields + similar job
   * content fields. Both sides use the same description_json fields shape:
   * { type, label, sections: [{ blocks: [{ html }] }] }
   */
  const mergedJobAndTemplatePreview = computed(() => {
    if (!templatePreview.value && !previewJob.value) return null;
    if (!templatePreview.value) return previewJob.value;
    if (!previewJob.value) return templatePreview.value;

    const similarByType = Object.fromEntries(
      (previewJob.value.fields || []).map((f) => [f.type, f]),
    );

    const mergedFields = (templatePreview.value.fields || []).map((templateField) => {
      const { type } = templateField;
      const isHeaderOrFooter = type && (
        type.endsWith('__header') || type.endsWith('__footer')
      );

      if (!isHeaderOrFooter && type && similarByType[type]) {
        return { ...templateField, sections: similarByType[type].sections ?? [] };
      }
      return templateField;
    });

    return {
      ...templatePreview.value,
      fields: mergedFields,
    };
  });

  // ─── creating ────────────────────────────────────────────────────────────────
  const createLoading = ref(false);
  const firstDraftLoading = ref(false);

  // ─── actions ─────────────────────────────────────────────────────────────────

  const searchJobPosts = async () => {
    if (!formTitle.value && !formJobCode.value) {
      searchResults.value = [];
      return;
    }
    searchLoading.value = true;
    try {
      const { jobs } = await searchJobPostsForWizard({
        title: formTitle.value,
        jobCode: formJobCode.value,
      });
      searchResults.value = jobs || [];
    } catch (err) {
      addErrorSnackbar({ text: `Job search error: ${err.message || err}` });
      throw err;
    } finally {
      searchLoading.value = false;
    }
  };

  const selectJobForPreview = async (job) => {
    if (!job) {
      selectedJobId.value = null;
      previewJob.value = null;
      previewLoading.value = false;
      return;
    }

    if (selectedJobId.value === job.id) return;
    selectedJobId.value = job.id;
    previewJob.value = null;
    previewLoading.value = true;
    try {
      previewJob.value = await getJobPostForWizard({ id: job.id });
    } catch (err) {
      addErrorSnackbar({ text: `Job preview error: ${err.message || err}` });
      selectedJobId.value = null;
      throw err;
    } finally {
      previewLoading.value = false;
    }
  };

  const goToStep2 = () => { step.value = 2; };
  const goToStep1 = () => { step.value = 1; };

  const loadTemplates = async () => {
    templatesLoading.value = true;
    try {
      const { templates: list } = await recruitJsonGetTemplates();
      templates.value = list || [];
    } catch (err) {
      addErrorSnackbar({ text: `Templates load error: ${err.message || err}` });
      throw err;
    } finally {
      templatesLoading.value = false;
    }
  };

  const selectTemplate = async (id) => {
    if (selectedTemplateId.value === id) return;
    selectedTemplateId.value = id;
    templatePreview.value = null;
    templatePreviewLoading.value = true;
    try {
      templatePreview.value = await getJobPostForWizard({ id });
    } catch (err) {
      addErrorSnackbar({ text: `Template preview error: ${err.message || err}` });
      selectedTemplateId.value = null;
      throw err;
    } finally {
      templatePreviewLoading.value = false;
    }
  };

  const createJobPost = async (router, { writeWithFirstDraft = false } = {}) => {
    createLoading.value = true;
    if (writeWithFirstDraft) firstDraftLoading.value = true;
    try {
      const { jobId } = await createJobPostFromWizard({
        title: formTitle.value?.trim(),
        location: formLocation.value?.trim(),
        jobCode: formJobCode.value?.trim(),
        templateId: selectedTemplateId.value,
        similarJobId: selectedJobId.value,
      });
      await router.push({
        name: 'editor-view-reload',
        params: { id: jobId },
        query: writeWithFirstDraft ? { writeWithFirstDraft: true } : undefined,
      });
    } catch (err) {
      addMultilineErrorSnackbar({
        text: `An error occurred while creating this job post.
        Our support team will contact you once the issue has been resolved.
        ${err.message}`,
      });
      throw err;
    } finally {
      createLoading.value = false;
      firstDraftLoading.value = false;
    }
  };

  const reset = () => {
    step.value = 1;
    formTitle.value = '';
    formJobCode.value = '';
    formLocation.value = '';
    searchResults.value = [];
    searchLoading.value = false;
    previewJob.value = null;
    previewLoading.value = false;
    selectedJobId.value = null;
    templates.value = [];
    templatesLoading.value = false;
    selectedTemplateId.value = null;
    templatePreview.value = null;
    templatePreviewLoading.value = false;
    createLoading.value = false;
    firstDraftLoading.value = false;
  };

  return {
    // state
    step,
    formTitle,
    formJobCode,
    formLocation,
    searchResults,
    searchLoading,
    previewJob,
    previewLoading,
    selectedJobId,
    templates,
    templatesLoading,
    selectedTemplateId,
    templatePreview,
    templatePreviewLoading,
    mergedJobAndTemplatePreview,
    createLoading,
    firstDraftLoading,

    // actions
    searchJobPosts,
    selectJobForPreview,
    goToStep1,
    goToStep2,
    loadTemplates,
    selectTemplate,
    createJobPost,
    reset,
  };
});
